import { useEffect, useMemo, useState } from "react"
import { CornerUpLeft, Download, ExternalLink, Share2, Trash2, X, ZoomIn, ZoomOut } from "lucide-react"
import { useAttachmentSrc } from "./hooks/useAttachmentSrc"
import { openAttachmentInTab } from "./services/private-attachment"
import { useSharedDocx } from "@/features/meeting/hooks/useSharedDocx"

/** What this viewer can show, decided by the filename. */
type DocKind = "pdf" | "docx" | "text" | "csv" | "unsupported"

function docKindOf(name: string): DocKind {
  const ext = name.split(".").pop()?.toLowerCase() ?? ""
  if (ext === "pdf") return "pdf"
  if (ext === "docx" || ext === "doc") return "docx"
  if (ext === "csv") return "csv"
  if (ext === "txt" || ext === "log" || ext === "md") return "text"
  return "unsupported"
}

/**
 * Split one CSV line, honouring quoted fields.
 *
 * Naive `split(",")` breaks the moment a cell contains a comma — which in a
 * spreadsheet exported by anyone is most of them.
 */
export function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ""
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else quoted = false
      } else cur += ch
    } else if (ch === '"') {
      quoted = true
    } else if (ch === ",") {
      out.push(cur); cur = ""
    } else cur += ch
  }
  out.push(cur)
  return out
}

/** Fetch a already-resolved (blob) URL as text. */
function useTextContent(url: string | undefined, enabled: boolean) {
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!url || !enabled) { setText(null); setError(null); return }
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error(String(res.status))
        const body = await res.text()
        if (!cancelled) setText(body)
      } catch {
        if (!cancelled) setError("Could not read this file.")
      }
    })()
    return () => { cancelled = true }
  }, [url, enabled])
  return { text, error }
}
import { ShareDialog } from "@/clientWeb/components/ui/ShareDialog"
import { downloadFile } from "./services/download-file"

export interface PreviewDocumentItem {
  id: string
  url: string
  name: string
  /** Sent by the viewer. Download is offered to recipients only. */
  isSentByMe?: boolean
  /** The message this attachment arrived in, when the caller knows it. */
  messageId?: string
}

interface DocumentPreviewOverlayProps {
  open: boolean
  pdf: PreviewDocumentItem | null
  onClose: () => void
  onDeleteCurrent?: (id: string) => void
  /** Jump to the message this item arrived in. Omitted, the button is not shown. */
  onShowInChat?: (id: string) => void
  /** False when the caller asks its own confirmation, so the user is not asked twice. */
  confirmDelete?: boolean
  /** Translated label for the Show in chat button. */
  showInChatLabel?: string
}

export function DocumentPreviewOverlay({
  open,
  pdf,
  onClose,
  onDeleteCurrent,
  onShowInChat,
  confirmDelete = true,
  showInChatLabel = "Show in chat",
}: DocumentPreviewOverlayProps) {
  const [zoom, setZoom] = useState(1)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = 1

  /**
   * The document, fetched with this client's token.
   *
   * Attachment URLs sit behind `requireAuth`, and an `<iframe src>` cannot
   * carry an Authorization header any more than an `<img>` can — so the frame
   * loaded bare, came back 401, and the viewer showed nothing. Called before
   * the early return below, because hooks must be.
   */
  const pdfSrc = useAttachmentSrc(pdf?.url)
  const kind = docKindOf(pdf?.name ?? "")
  // Word documents go through the same sanitising converter the meeting's
  // document share uses — mammoth's HTML is not trusted markup, it is built
  // from a file somebody else sent.
  const docx = useSharedDocx(kind === "docx" ? pdfSrc ?? "" : "", kind === "docx")
  const { text, error: textError } = useTextContent(pdfSrc, kind === "text" || kind === "csv")
  const csvRows = useMemo<string[][] | null>(
    () =>
      kind === "csv" && text
        ? text.split(/\r?\n/).filter((l) => l.length > 0).slice(0, 500).map(splitCsvLine)
        : null,
    [kind, text],
  )

  useEffect(() => {
    if (!open) return
    setZoom(1)
    setCurrentPage(1)
  }, [open])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, onClose])

  if (!open || !pdf) return null

  // Same reason as the video viewer: `<a download>` is ignored cross-origin,
  // and this anchor was never in the document either, so on several browsers
  // the click did nothing at all.
  const onDownload = () => {
    void downloadFile(pdf.url, pdf.name || "document.pdf")
  }

  const onShare = () => {
    setShareDialogOpen(true)
  }

  const onDeleteClick = () => {
    if (!confirmDelete) { onDeleteCurrent?.(pdf.id); return }
    setDeleteConfirmOpen(true)
  }

  const onDeleteConfirm = () => {
    setDeleteConfirmOpen(false)
    onDeleteCurrent?.(pdf.id)
  }

  // As in the media viewer: a private chat file opens through its signed link.
  const onOpenExternal = () => {
    openAttachmentInTab(pdf.url)
  }

  return (
    // `pointer-events-auto`: portaled to the body, and an open Radix drawer
    // sets `pointer-events: none` there — which left every control in here,
    // the X included, inert when the viewer was opened from the info panel.
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm pointer-events-auto">
      <div className="flex h-full flex-col">
        <header className="flex h-14 items-center justify-between border-b border-white/15 px-4 text-white">
          <div className="min-w-0 pr-4 text-sm font-medium truncate">{pdf.name}</div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            {/* Recipients only — the sender still has the file they sent. */}
            {!pdf.isSentByMe && (
            <button
              type="button"
              onClick={onDownload}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            )}
            {onShowInChat && (
              <button
                type="button"
                onClick={() => onShowInChat(pdf.id)}
                className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
                title={showInChatLabel}
              >
                <CornerUpLeft className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onShare}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Share"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onOpenExternal}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onDeleteClick}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-red-500/30"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-md hover:bg-white/15"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="relative flex-1 overflow-hidden">
          <button
            type="button"
            onClick={onClose}
            className="absolute inset-0 h-full w-full cursor-default"
            aria-label="Close preview"
          />

          <div className="absolute inset-0 flex items-center justify-center p-6">
            {!pdfSrc ? (
              <div className="flex flex-col items-center gap-3 text-white/70">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white/80" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : kind === "pdf" ? (
              <iframe
                src={pdfSrc}
                title={pdf.name}
                className="w-full h-full border-0 bg-white"
                style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
              />
            ) : kind === "docx" ? (
              <div
                className="h-full w-full max-w-3xl overflow-auto rounded-lg bg-white p-8 text-sm text-slate-900"
                style={{ zoom }}
                onClick={(e) => e.stopPropagation()}
              >
                {docx.loading && <p className="text-slate-500">Reading document…</p>}
                {docx.error && <p className="text-red-600">{docx.error}</p>}
                {docx.content}
              </div>
            ) : kind === "csv" ? (
              <div
                className="h-full w-full overflow-auto rounded-lg bg-white text-slate-900"
                style={{ zoom }}
                onClick={(e) => e.stopPropagation()}
              >
                {textError && <p className="p-6 text-red-600">{textError}</p>}
                {csvRows && (
                  <table className="w-full border-collapse text-xs">
                    <tbody>
                      {csvRows.map((row, r) => (
                        <tr key={r} className={r === 0 ? "bg-slate-100 font-semibold" : "even:bg-slate-50"}>
                          {row.map((cell, c) => (
                            <td key={c} className="border border-slate-200 px-2 py-1 align-top whitespace-pre-wrap">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : kind === "text" ? (
              <pre
                className="h-full w-full max-w-4xl overflow-auto rounded-lg bg-white p-6 text-xs leading-relaxed text-slate-900 whitespace-pre-wrap"
                style={{ zoom }}
                onClick={(e) => e.stopPropagation()}
              >
                {textError ?? text}
              </pre>
            ) : (
              // Spreadsheets land here. Saying so is better than showing an
              // empty frame and letting the reader decide whether the file is
              // broken or the viewer is.
              <div
                className="max-w-sm rounded-2xl bg-white/10 p-8 text-center text-white"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-sm font-medium">No preview for this file type</p>
                <p className="mt-1 text-xs text-white/70">
                  {pdf.name.split(".").pop()?.toUpperCase()} files can be downloaded and opened in their own app.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="h-8 border-t border-white/15 px-4 text-xs text-white/70 flex items-center justify-between">
          <span>{kind === "pdf" ? `Page ${currentPage} of ${totalPages}` : pdf.name}</span>
          <span className="text-white/50">
            {kind === "unsupported" ? "Preview unavailable" : "Document preview"}
          </span>
        </div>
      </div>

      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        url={pdf.url}
        title="Share PDF"
      />

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-card rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 mx-4 p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Delete PDF</h3>
              <p className="text-sm text-muted-foreground">Would you like to delete this PDF?</p>
              <div className="flex items-center gap-3 mt-2 w-full">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
